import { Modal } from "@msys/ui";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Formik } from "formik";
import { uniqueId } from "lodash-es";
import React from "react";
import {
  AttachmentSnapshot,
  EmailTemplateContext,
} from "../../../clients/graphqlTypes.js";
import {
  closeAfterSend,
  CrmSendEmailForm,
  type EmailContext,
  type FormValues,
  isEmailAttachmentsTooLarge,
  RecipientOption,
  type UrlAttachment,
  useCrmSendEmail,
} from "./CrmSendEmailForm.js";

interface Props {
  handleClose: () => void;
  handleComplete?: (
    handleClose: () => void,
    values: FormValues
  ) => void | Promise<void>;
  title?: string;
  recipient?: RecipientOption;
  recipientOptions: RecipientOption[];
  urlAttachments?: UrlAttachment[];
  context: EmailContext | null;
  isLoading?: boolean;
  getAttachmentsOnSubmit?: () => Promise<AttachmentSnapshot[]>;
  additionalFields?: React.ReactNode;
  emailTemplateContext: EmailTemplateContext;
  refetchQueries?: string[];
  projectId?: string;
}

export const CrmSendEmailModal = (props: Props) => {
  const { t } = useTranslate(["CrmOrganisations", "Global"]);

  const {
    handleClose,
    handleComplete,
    title,
    recipient,
    recipientOptions,
    urlAttachments,
    context,
    isLoading,
    getAttachmentsOnSubmit,
    additionalFields,
    emailTemplateContext,
    refetchQueries,
  } = props;

  const {
    initialValues,
    validationSchema,
    handleSendEmail,
    emailTemplates,
    templatesLoading,
    templatesError,
  } = useCrmSendEmail({
    recipient,
    urlAttachments,
    context,
    getAttachmentsOnSubmit,
    emailTemplateContext,
    refetchQueries,
  });

  const handleSubmit = async (values: FormValues) => {
    if (!values.recipient || !values.recipient.crmCompanyId) return;

    if (!(await handleSendEmail(values))) return;

    await closeAfterSend(
      handleClose,
      handleComplete && (close => handleComplete(close, values))
    );
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      {formikProps => (
        <Modal
          id={"send-crm-mail-modal"}
          title={title ?? t("Send email", { ns: "CrmOrganisations" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Send", { ns: "Global" }),
              buttonProps: {
                type: "submit",
                form: formId,
                loading: formikProps.isSubmitting,
                disabled:
                  !formikProps.isValid ||
                  isEmailAttachmentsTooLarge(formikProps.values.attachments),
              },
            },
          ]}
          isLoading={isLoading || templatesLoading}
          maxWidth="md"
        >
          <Stack direction="column" spacing={2}>
            <CrmSendEmailForm
              recipientOptions={recipientOptions}
              additionalFields={additionalFields}
              formId={formId}
              availableTemplates={emailTemplates}
              context={context}
              emailTemplateContext={emailTemplateContext}
              templatesLoading={templatesLoading}
              templatesError={templatesError}
            />
          </Stack>
        </Modal>
      )}
    </Formik>
  );
};
